// /production-planning — Production Planning (PAGES.md §3.68)
//
// Aggregate production planning dashboard for festive orders. Shows ingredient
// requirements grouped by preparation category, procurement plan, production
// timeline, capacity planning, and staff suggestions.
// RESTAURANT vertical only — MANAGER+ (level 30). Feature flag: ENABLE_FESTIVE_MODULE.
// Traceability: FR-FEST-011 to FR-FEST-013
// User Story: US-ORD-019 (Kitchen Manager prep dashboard)

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  DatePicker,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Alert,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { festiveService } from '@/services/festive.service';
import type { Festival, ProductionPlan } from '@/services/festive.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FILTER, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';
import { reportsApi } from '@/features/reports';
import { ROLE_LEVELS } from './constants';
import {
  PackageIcon,
  CurrencyIcon,
  MenuIcon,
  DownloadIcon,
  ClockIcon,
  UsersIcon,
  ClipboardIcon,
} from './icons';

const AUTO_REFRESH_MS = 30_000; // 30s polling per US-ORD-019

// Preparation categories for grouping (US-ORD-019: sweets, mains, beverages)
const PREP_CATEGORIES = ['Sweets', 'Mains', 'Starters', 'Beverages', 'Desserts', 'Other'] as const;
type PrepCategory = typeof PREP_CATEGORIES[number];

// ─── Ingredient flat row for DataTable ──────────────────────────────────────

interface IngredientRow {
  id: string;
  product_name: string;
  ingredient_name: string;
  required_qty: number;
  unit: string;
  in_stock: number;
  shortfall: number;
  category: PrepCategory;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// TODO: Category inference should be backend-driven via product.category or recipe.category
// This regex heuristic is an MVP fallback — it will misclassify products with non-matching names
function inferCategory(productName: string): PrepCategory {
  const lower = productName.toLowerCase();
  if (/sweet|laddu|barfi|gulab|rasgulla|halwa|peda|jalebi|kheer/i.test(lower)) return 'Sweets';
  if (/juice|lassi|chai|tea|coffee|drink|sharbat|soda|water|buttermilk/i.test(lower)) return 'Beverages';
  if (/cake|ice.?cream|kulfi|rabri|falooda|pudding/i.test(lower)) return 'Desserts';
  if (/pakora|samosa|tikki|chaat|vada|cutlet|kebab|spring.?roll/i.test(lower)) return 'Starters';
  if (/biryani|curry|dal|roti|naan|rice|paneer|chicken|mutton|fish|thali|pulao/i.test(lower)) return 'Mains';
  return 'Other';
}

// TODO: Staff-per-slot ratio should be a tenant-level config, not a magic number
const ITEMS_PER_STAFF = 8;

function estimateStaffForSlot(itemCount: number): number {
  return Math.max(1, Math.ceil(itemCount / ITEMS_PER_STAFF));
}

// ─── Print stylesheet (injected once) ────────────────────────────────────────

const PRINT_STYLE_ID = 'production-planning-print';

function ensurePrintStyles() {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      body * { visibility: hidden; }
      #production-print-area, #production-print-area * { visibility: visible; }
      #production-print-area { position: absolute; left: 0; top: 0; width: 100%; }
      .no-print { display: none !important; }
    }
  `;
  document.head.appendChild(style);
}

// ─── Mobile Ingredient Card ────────────────────────────────────────────────

function IngredientMobileCard({ row }: { row: IngredientRow }) {
  return (
    <div className={cn(GLASS, 'p-4 animate-slide-up')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{row.product_name}</p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">{row.ingredient_name}</p>
        </div>
        {row.shortfall > 0 ? (
          <Badge variant="danger" size="sm">-{row.shortfall.toLocaleString('en-IN')}</Badge>
        ) : (
          <Badge variant="success" size="sm">OK</Badge>
        )}
      </div>
      <div className="flex items-center gap-4 mt-2">
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          Need: <span className="font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">{row.required_qty.toLocaleString('en-IN')} {row.unit}</span>
        </span>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          Have: <span className={cn(
            'font-mono tabular-nums font-medium',
            row.in_stock >= row.required_qty
              ? 'text-success-600 dark:text-success-400'
              : 'text-danger-600 dark:text-danger-400',
          )}>{row.in_stock.toLocaleString('en-IN')}</span>
        </span>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function ProductionPlanningContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEventId = searchParams.get('event') ?? '';
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  // Role-based access
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canGeneratePO = roleLevel >= 50; // OWNER+
  const canExport = roleLevel >= 30; // MANAGER+

  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [plan, setPlan] = useState<ProductionPlan | null>(null);
  const [planDate, setPlanDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingPO, setIsGeneratingPO] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<PrepCategory>>(new Set());
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { document.title = 'Production Planning — Orio360 Admin'; }, []);

  // Fetch festivals
  useEffect(() => {
    festiveService.listFestivals().then((r) => setFestivals(r.data)).catch(() => { /* silent — festival selector stays empty */ });
  }, []);

  const festivalOptions = useMemo(() =>
    festivals.map((f) => ({ value: f.id, label: f.name })),
  [festivals]);

  // Fetch plan
  const fetchPlan = useCallback(async () => {
    if (!selectedEventId) return;
    setIsLoading(true);
    try {
      const data = await festiveService.getProductionPlanning(selectedEventId, {
        date: planDate || undefined,
      });
      setPlan(data);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load plan.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedEventId, planDate, toast]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  // Auto-refresh every 30s (US-ORD-019: auto-refreshes when new orders confirmed)
  useEffect(() => {
    if (!selectedEventId) return;
    refreshTimerRef.current = setInterval(() => {
      fetchPlan();
    }, AUTO_REFRESH_MS);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [selectedEventId, fetchPlan]);

  // Flatten ingredients + assign categories
  const ingredientRows: IngredientRow[] = useMemo(() => {
    if (!plan) return [];
    const rows: IngredientRow[] = [];
    for (const item of plan.items) {
      const category = inferCategory(item.product_name);
      for (const ing of item.ingredients_needed) {
        rows.push({
          id: `${item.product_name}-${ing.name}`,
          product_name: item.product_name,
          ingredient_name: ing.name,
          required_qty: ing.quantity,
          unit: ing.unit,
          in_stock: ing.in_stock,
          shortfall: Math.max(0, ing.quantity - ing.in_stock),
          category,
        });
      }
    }
    return rows;
  }, [plan]);

  const shortfallRows = ingredientRows.filter((r) => r.shortfall > 0);

  // Group by category
  const groupedRows = useMemo(() => {
    const map = new Map<PrepCategory, IngredientRow[]>();
    for (const cat of PREP_CATEGORIES) map.set(cat, []);
    for (const row of ingredientRows) {
      map.get(row.category)!.push(row);
    }
    // Remove empty categories
    const entries: [PrepCategory, IngredientRow[]][] = [];
    for (const [cat, rows] of map) {
      if (rows.length > 0) entries.push([cat, rows]);
    }
    return entries;
  }, [ingredientRows]);

  // Toggle category collapse
  const toggleCategory = useCallback((cat: PrepCategory) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }, []);

  // Table columns (TYPOGRAPHY.md §22 compliant)
  const columns: ColumnDef<IngredientRow>[] = useMemo(() => [
    {
      key: 'product_name', header: 'Product',
      render: (_v: unknown, row: IngredientRow) => (
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{row.product_name}</span>
      ),
    },
    {
      key: 'ingredient_name', header: 'Ingredient',
      render: (_v: unknown, row: IngredientRow) => (
        <span className="text-sm font-normal text-neutral-600 dark:text-neutral-400">{row.ingredient_name}</span>
      ),
    },
    {
      key: 'required_qty', header: 'Required', numeric: true,
      render: (_v: unknown, row: IngredientRow) => (
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 font-mono tabular-nums">
          {row.required_qty.toLocaleString('en-IN')} {row.unit}
        </span>
      ),
    },
    {
      key: 'in_stock', header: 'Available', numeric: true,
      render: (_v: unknown, row: IngredientRow) => (
        <span className={cn(
          'text-sm font-medium font-mono tabular-nums',
          row.in_stock >= row.required_qty
            ? 'text-success-600 dark:text-success-400'
            : 'text-danger-600 dark:text-danger-400',
        )}>
          {row.in_stock.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'shortfall', header: 'To Procure', numeric: true,
      render: (_v: unknown, row: IngredientRow) => (
        row.shortfall > 0
          ? <Badge variant="danger" size="sm">-{row.shortfall.toLocaleString('en-IN')}</Badge>
          : <Badge variant="success" size="sm">OK</Badge>
      ),
    },
  ], []);

  // Export CSV
  const handleExport = useCallback(() => {
    if (!ingredientRows.length) return;
    const header = 'Category,Product,Ingredient,Required,Unit,Available,Shortfall\n';
    const csv = ingredientRows.map((r) =>
      `"${r.category}","${r.product_name}","${r.ingredient_name}",${r.required_qty},"${r.unit}",${r.in_stock},${r.shortfall}`,
    ).join('\n');
    const blob = new Blob([header + csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `production-plan-${selectedEventId}-${planDate || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Plan exported', variant: 'success' });
  }, [ingredientRows, selectedEventId, planDate, toast]);

  // Generate PO (OWNER+ only)
  const handleGeneratePO = useCallback(async () => {
    if (!canGeneratePO || !shortfallRows.length) return;
    setIsGeneratingPO(true);
    try {
      await reportsApi.autoGeneratePO({ location_id: selectedLocationId });
      toast({ title: 'Purchase order generated for shortfall items', variant: 'success' });
      fetchPlan(); // Refresh after PO generation
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to generate PO', variant: 'error' });
    } finally {
      setIsGeneratingPO(false);
    }
  }, [canGeneratePO, shortfallRows.length, selectedLocationId, toast, fetchPlan]);

  // Print checklist (US-ORD-019)
  const handlePrint = useCallback(() => {
    ensurePrintStyles();
    window.print();
  }, []);

  // Keyboard shortcut: p = print
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'p' && plan) { e.preventDefault(); handlePrint(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [plan, handlePrint]);

  return (
    <PageContainer
      title="Production Planning"
      subtitle="Ingredient requirements and procurement planning for festive orders"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Production Planning' },
      ]}
      actions={
        <div className="flex items-center gap-2 no-print">
          {canExport && (
            <Button variant="secondary" size="sm" onClick={handlePrint} disabled={!plan} aria-label="Print production plan (shortcut: p)">
              <ClipboardIcon className="h-4 w-4" /><span className="ml-1.5 hidden sm:inline">Print</span>
            </Button>
          )}
          {canExport && (
            <Button variant="secondary" size="sm" onClick={handleExport} disabled={!ingredientRows.length} aria-label="Export plan as CSV">
              <DownloadIcon /><span className="ml-1.5 hidden sm:inline">Export</span>
            </Button>
          )}
          {canGeneratePO && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleGeneratePO}
              disabled={!shortfallRows.length || isGeneratingPO}
              loading={isGeneratingPO}
              aria-label="Generate purchase order for shortfall items"
            >
              Generate PO
            </Button>
          )}
        </div>
      }
    >
      <div className="animate-fade-in" id="production-print-area">
        {/* Filters */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6 no-print')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-56">
              <Select
                options={festivalOptions}
                value={selectedEventId}
                onChange={(val) => {
                  const v = typeof val === 'string' ? val : val[0] ?? '';
                  setSearchParams(v ? { event: v } : {}, { replace: true });
                }}
                placeholder="Select Festival"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Date</span>
              <DatePicker
                value={planDate ? new Date(planDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => setPlanDate(d.toLocaleDateString('en-CA'))}
              />
            </div>
          </div>
        </div>

        {!selectedEventId ? (
          <EmptyState
            variant="page"
            icon={<PackageIcon className="h-14 w-14" />}
            title="No production plans"
            description="Select a festival event to view production requirements"
          />
        ) : isLoading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => <StatCard key={i} title="" value="" loading />)}
            </div>
            <div className={cn(GLASS, 'p-6 space-y-4')}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton variant="text" width="20%" height={18} />
                  <Skeleton variant="text" width="20%" height={18} />
                  <Skeleton variant="text" width="15%" height={18} />
                  <Skeleton variant="text" width="12%" height={18} />
                  <Skeleton variant="rect" width={60} height={24} />
                </div>
              ))}
            </div>
          </div>
        ) : plan ? (
          <>
            {/* Shortfall Alert (spec: Alert — low ingredient warnings) */}
            {shortfallRows.length > 0 && (
              <div className="mb-6 animate-slide-up no-print">
                <Alert
                  variant="danger"
                  title={`${shortfallRows.length} ingredient${shortfallRows.length !== 1 ? 's' : ''} below required stock`}
                  description={`Review shortfalls and ${canGeneratePO ? 'generate a purchase order' : 'notify the owner to generate a purchase order'}.`}
                />
              </div>
            )}

            {/* Planning Date Header (TYPOGRAPHY §22) */}
            {plan.date && (
              <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                Production Plan: {new Date(plan.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="animate-slide-up">
                <StatCard
                  title="Total Items"
                  value={plan.total_items.toLocaleString('en-IN')}
                  icon={<PackageIcon className="h-4 w-4" />}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
                <StatCard
                  title="Total Ingredients"
                  value={plan.total_ingredients.toLocaleString('en-IN')}
                  icon={<MenuIcon className="h-4 w-4" />}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
                <StatCard
                  title="Procurement Cost"
                  value={formatPaise(plan.procurement_cost_estimate, currency)}
                  icon={<CurrencyIcon className="h-4 w-4" />}
                />
              </div>
            </div>

            {/* Grouped Ingredient Tables (US-ORD-019: grouped by prep category) */}
            <div className="hidden sm:block space-y-4 mb-6">
              {groupedRows.map(([category, rows], catIdx) => {
                const isCollapsed = collapsedCategories.has(category);
                const catShortfall = rows.filter((r) => r.shortfall > 0).length;
                const panelId = `category-panel-${category.toLowerCase()}`;
                return (
                  <div key={category} className={cn(GLASS, 'overflow-hidden animate-slide-up')} style={{ animationDelay: `${catIdx * 60}ms` }}>
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      aria-expanded={!isCollapsed}
                      aria-controls={panelId}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-neutral-50/50 dark:hover:bg-neutral-800/50 transition-colors duration-200"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                          {category}
                        </span>
                        <span className="rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-200 px-2 py-0.5 text-xs font-semibold font-mono tabular-nums">
                          {rows.length}
                        </span>
                        {catShortfall > 0 && (
                          <Badge variant="danger" size="sm">{catShortfall} shortfall</Badge>
                        )}
                      </div>
                      <svg
                        className={cn('h-4 w-4 text-neutral-400 transition-transform duration-200', !isCollapsed && 'rotate-180')}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {!isCollapsed && (
                      <div id={panelId} role="region" aria-label={`${category} ingredients`}>
                        <DataTable<IngredientRow> columns={columns} data={rows} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile Cards (xs-sm) */}
            <div className="block sm:hidden space-y-4 mb-6">
              {groupedRows.map(([category, rows]) => (
                <div key={category}>
                  <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1 mb-2">
                    {category} ({rows.length})
                  </h3>
                  <div className="space-y-2">
                    {rows.map((row) => (
                      <IngredientMobileCard key={row.id} row={row} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Prep Schedule */}
            {plan.prep_schedule.length > 0 && (
              <div className={cn(GLASS, 'p-5 mb-6 animate-slide-up')}>
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <ClockIcon className="h-4 w-4 text-violet-500" />
                  Prep Schedule
                </h3>
                <div className="space-y-3">
                  {plan.prep_schedule.map((slot) => {
                    const staffNeeded = estimateStaffForSlot(slot.items.length);
                    return (
                      <div key={slot.time_slot}>
                        <div className="flex items-start gap-3">
                          {/* Time slot label: TYPOGRAPHY §22 — text-xs font-normal neutral-500 */}
                          <span className="text-xs font-normal text-neutral-500 dark:text-neutral-400 font-mono tabular-nums w-20 flex-shrink-0 pt-0.5">
                            {slot.time_slot}
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {slot.items.map((item) => (
                              <Badge key={item} variant="draft" size="sm">{item}</Badge>
                            ))}
                          </div>
                        </div>
                        {/* Staff suggestion: TYPOGRAPHY §22 — text-sm font-normal info-700 on info-50 bg */}
                        <div className="ml-[calc(5rem+0.75rem)] mt-1.5">
                          <span className="inline-flex items-center gap-1.5 text-sm font-normal text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/20 px-2.5 py-1 rounded-lg">
                            <UsersIcon className="h-3.5 w-3.5" />
                            Suggested: {staffNeeded} staff for this slot
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cross-navigation to Demand Forecast */}
            <div className={cn(GLASS, 'p-4 no-print')}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Need demand predictions?</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">View forecast analysis based on historical sales data</p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => navigate('/reports/demand-forecast')} aria-label="View demand forecast report">
                  View Forecast
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </PageContainer>
  );
}

export function ProductionPlanningPage() {
  return (
    <ErrorBoundary>
      <ProductionPlanningContent />
    </ErrorBoundary>
  );
}
